import type { DailyTip, DailySales, Employee } from "../types";
import { employeeDisplayName } from "../employeePeriod";

interface Props {
  employees: Employee[];
  tips: DailyTip[];
  sales: DailySales[];
  startDate: string;
  endDate: string;
}

function money(n: number): string {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

export function PayPeriodSummary({ employees, tips, sales }: Props) {
  const totalSales = sales.reduce((s, d) => s + Number(d.total_sales), 0);
  const totalRawTips = tips.reduce((s, t) => s + Number(t.raw_tips), 0);
  const totalCalcTips = tips.reduce((s, t) => s + (Number(t.calculated_tips) || 0), 0);

  const byEmployee = new Map<string, { name: string; hours: number; calculated: number }>();
  for (const emp of employees) {
    byEmployee.set(emp.id, { name: employeeDisplayName(emp), hours: 0, calculated: 0 });
  }
  for (const t of tips) {
    let entry = byEmployee.get(t.employee_id);
    if (!entry) {
      entry = { name: "Former employee", hours: 0, calculated: 0 };
      byEmployee.set(t.employee_id, entry);
    }
    entry.hours += Number(t.hours_worked);
    entry.calculated += Number(t.calculated_tips) || 0;
  }

  const empRows = Array.from(byEmployee.values())
    .filter((e) => e.hours > 0 || e.calculated > 0)
    .sort((a, b) => b.calculated - a.calculated);

  const tipPct = totalSales > 0 ? ((totalRawTips / totalSales) * 100).toFixed(1) : "—";

  return (
    <div className="rounded-lg border border-tribal-border bg-tribal-surface p-4">
      <h3 className="mb-3 text-sm font-semibold">Pay Period Summary</h3>

      {/* Metric cards */}
      <div className="mb-4 grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
        <Card label="Total Sales" value={money(totalSales)} />
        <Card label="Total Tips (raw)" value={money(totalRawTips)} />
        <Card label="Total Tips (calc)" value={money(totalCalcTips)} />
        <Card label="Tip %" value={tipPct + "%"} />
      </div>

      {/* Per-employee */}
      {empRows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-tribal-border text-left text-tribal-muted">
                <th className="pb-1 font-medium">Employee</th>
                <th className="pb-1 text-right font-medium">Hours</th>
                <th className="pb-1 text-right font-medium">Tips</th>
                <th className="pb-1 text-right font-medium">$/hr</th>
              </tr>
            </thead>
            <tbody>
              {empRows.map((e) => (
                <tr key={e.name} className="border-b border-tribal-border last:border-0">
                  <td className="py-1">{e.name}</td>
                  <td className="py-1 text-right tabular-nums">{e.hours.toFixed(2)}</td>
                  <td className="py-1 text-right tabular-nums font-medium">{money(e.calculated)}</td>
                  <td className="py-1 text-right tabular-nums text-tribal-muted">
                    {e.hours > 0 ? money(e.calculated / e.hours) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {empRows.length === 0 && (
        <p className="text-xs text-tribal-muted">No tip data entered yet.</p>
      )}
    </div>
  );
}

function Card({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md bg-white/[0.03] px-3 py-2">
      <div className="text-[10px] text-tribal-muted">{label}</div>
      <div className="mt-0.5 text-sm font-semibold tabular-nums">{value}</div>
    </div>
  );
}
